import { getEmployees, setNewEmployeePopover } from "../employeeSlice";
import { useAppDispatch } from "hooks/hooks";
import {
  Controller,
  UseFormHandleSubmit,
  Control,
  UseFormRegister,
  FieldErrorsImpl,
  UseFormSetFocus,
  UseFormGetValues,
  UseFormSetValue
} from "react-hook-form";
import DatePicker from "react-datepicker";
import { ChangeEvent, useEffect, useState } from "react";
import { FormatDateIso } from "utils/dateUtils";
import { hrService } from "../../../services/hr.services";
import { toast } from "react-toastify";
import { styles } from "../../../../../../components/Popover/popoverSelectStyle";
import { GenderType } from "Dashboard/scenes/HumanResources/domain/GenderType";
import { ICreateEmployeeRequestParams } from "Dashboard/scenes/HumanResources/domain/ICreateEmployeeRequestParams";
import { StatusType } from "Dashboard/scenes/HumanResources/domain/StatusType";
import { SalaryType } from "Dashboard/scenes/HumanResources/domain/SalaryType";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesHr from "Dashboard/scenes/HumanResources/popover.module.scss";
import EmployeeRoleSelectCreate from "./EmployeeRoleSelectCreate";
import { selectState as selectStateDashboard } from "Dashboard/dashboard.slice";
import { useSelector } from "react-redux";
import NumberInput from "Dashboard/components/NumberInput";

interface IProps {
  handleSubmit: UseFormHandleSubmit<ICreateEmployeeRequestParams>;
  register: UseFormRegister<ICreateEmployeeRequestParams>;
  control: Control<ICreateEmployeeRequestParams, any>;
  errors: Partial<FieldErrorsImpl<ICreateEmployeeRequestParams>>;
  reset: () => void;
  employeeRoleValue: number | undefined;
  setEmployeeRoleValue: React.Dispatch<React.SetStateAction<number | undefined>>;
  salaryTypeValue: SalaryType;
  setSalaryTypeValue: React.Dispatch<React.SetStateAction<SalaryType>>;
  setFocus: UseFormSetFocus<ICreateEmployeeRequestParams>;
  getValues: UseFormGetValues<ICreateEmployeeRequestParams>;
  setValue: UseFormSetValue<ICreateEmployeeRequestParams>;
};

const NewEmployeePopover = (props: IProps) => {
  const dispatch = useAppDispatch();
  const { provisions } = useSelector(selectStateDashboard);

  const onValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    props.setSalaryTypeValue(
      SalaryType[event.target.value as keyof typeof SalaryType]);
  };

  const {
    handleSubmit,
    register,
    control,
    errors,
    reset,
    setFocus,
    getValues,
    setValue,
  } = props;

  const close = () => {
    reset();
    dispatch(setNewEmployeePopover({ isOpen: false }));
  };

  useEffect(() => {
    setFocus("employeeName");
  }, [setFocus]);

  const onSubmit = async (data: ICreateEmployeeRequestParams) => {
    try {
      data.status = StatusType.ACTIVE;
      data.salaryType = props.salaryTypeValue;
      await hrService.createEmployee(data);
      close();
      toast.success("Zaposleni je uspešno dodat");
      dispatch(getEmployees({}));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <form
      className={`${classesHr.newEmployeePopover} ${classes.basicPopover}`}
      onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>ime i prezime</label>
          <input type="text" className={`${classes.itemBorder}
            ${errors.employeeName ? classes.itemError : ""}`}
            {...register("employeeName", { required: true })}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>pol</label>
          <select className={`${classes.itemBorder}
            ${errors.gender ? classes.itemError : ""}`}
            {...register("gender", { required: true })}>
            <option value={GenderType.MALE}>Muško</option>
            <option value={GenderType.FEMALE}>Žensko</option>
          </select>
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}
          ${errors.employeeRoleId ? classes.itemError : ""}`}>
          <label className={classes.mr5}>radno mesto</label>
          <Controller
            name="employeeRoleId"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <EmployeeRoleSelectCreate
                styles={styles.customStyleClassic}
                error={errors.employeeRoleId}
                value={props.employeeRoleValue}
                setValue={props.setEmployeeRoleValue}
                onChange={onChange}
                preserveInput={true}
              />
            )}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>prijavljen</label>
          <div>
            <input type="checkbox" {...register("registered")} />
          </div>
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>prijavljen do</label>
          <Controller
            control={control}
            name="employedUntil"
            defaultValue={undefined}
            rules={{
              required: false,
            }}
            render={({ field: { onChange, value } }) => (
              <div>
                <DatePicker
                  onChange={(date) =>
                    onChange(date ? FormatDateIso(date) : undefined)
                  }
                  dateFormat="dd.MM.yyyy"
                  showMonthDropdown
                  showYearDropdown
                  selected={value ? new Date(value) : null}
                  className={`${classes.dateItem} ${classes.itemBorder}
                    ${errors.employedUntil ? classes.itemError : ""}`}
                />
              </div>
            )}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>počeo sa radom</label>
          <Controller
            control={control}
            name="employedAt"
            defaultValue={undefined}
            rules={{
              required: false,
            }}
            render={({ field: { onChange, value } }) => (
              <div>
                <DatePicker
                  onChange={(date) =>
                    onChange(date ? FormatDateIso(date) : undefined)
                  }
                  dateFormat="dd.MM.yyyy"
                  showMonthDropdown
                  showYearDropdown
                  selected={value ? new Date(value) : null}
                  className={`${classes.dateItem} ${classes.itemBorder}
                    ${errors.employedAt ? classes.itemError : ""}`}
                />
              </div>
            )}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>zdravstveno osigran do</label>
          <div className={classesHr.calendarContainer}>
            <Controller
              control={control}
              name="healthInsuranceUntil"
              defaultValue={undefined}
              rules={{
                required: false,
              }}
              render={({ field: { onChange, value } }) => (
                <div>
                  <DatePicker
                    onChange={(date) =>
                      onChange(date ? FormatDateIso(date) : undefined)
                    }
                    dateFormat="dd.MM.yyyy"
                    showMonthDropdown
                    showYearDropdown
                    selected={value ? new Date(value) : null}
                    className={`${classes.dateItem} ${classes.itemBorder}
                    ${errors.healthInsuranceUntil ? classes.itemError : ""}`}
                  />
                </div>
              )}
            />
          </div>
        </div>
      </div>

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>plaćanje</label>
          <div className={classesHr.radioButtonsDiv}>
            <input
              type="hidden"
              value={props.salaryTypeValue}
              {...register("salaryType", { required: false })}
            />
            <input
              type="radio"
              value={SalaryType.HOURLY}
              checked={props.salaryTypeValue === SalaryType.HOURLY}
              onChange={onValueChange}
              name="type"
            />
            <label>karneti</label>
            <input
              type="radio"
              value={SalaryType.MONTHLY}
              checked={props.salaryTypeValue === SalaryType.MONTHLY}
              onChange={onValueChange}
              name="type"
            />
            <label>mesečno</label>
          </div>
        </div>
      </div>

      {
        props.salaryTypeValue === SalaryType.HOURLY && (
          <>
            {provisions?.activeHourlyRateNames.map(rateName => (
              <div className={classes.row}>
                <div className={`${classes.col} ${classes.rowDir}`}>
                  <label className={classes.mr5}>{rateName.name.toLowerCase()} cena</label>
                  <NumberInput
                    formatAs="quantity"
                    value={getValues("hourlyRates")?.[rateName.index]?.toString()}
                    onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                      if (parsedValue !== getValues("hourlyRates")?.[rateName.index]) {
                        const oldHourlyRates = getValues("hourlyRates");
                        let newHourlyRates = oldHourlyRates !== undefined ? [...oldHourlyRates] : [];
                        newHourlyRates[rateName.index] = parsedValue;
                        setValue("hourlyRates", newHourlyRates);
                      }
                    }}
                    className={`${classes.itemBorder} ${errors.hourlyRates ? classes.itemError : ""}`}
                  />
                </div>
              </div>
            ))}
          </>
        )
      }

      <div className={classes.row}>
        <div className={`${classes.col} ${classes.rowDir}`}>
          <label className={classes.mr5}>napomena</label>
          <textarea {...register("note")}></textarea>
        </div>
      </div>

      <div className={classes.row}>
        <div className={classes.buttons}>
          <button className={classes.iconButton} type="submit">
            <FontAwesomeIcon
              icon={faLongArrowAltDown}
              className={classes.icon} />
          </button>
        </div>
      </div>
    </form >
  );
};

export default NewEmployeePopover;
